import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Package, ListTodo, Loader2 } from 'lucide-react';
import api from '@/lib/api';

interface StatusData {
  active_tasks: number;
  orders_processing: number;
  success_rate_24h: number;
  needs_attention: {
    escalated_tasks: Array<{ id: string; task_type: string; error: string | null }>;
    failed_orders: Array<{ id: string; order_number: string | null }>;
  };
}

export default function DippyPanelStatus() {
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get<{
        stats: { active_tasks: number; orders_processing: number; success_rate_24h: number | string };
        needs_attention: StatusData['needs_attention'];
      }>('/admin/agent/dashboard');
      setData({
        active_tasks: res.stats.active_tasks,
        orders_processing: res.stats.orders_processing,
        success_rate_24h: Number(res.stats.success_rate_24h ?? 0),
        needs_attention: res.needs_attention ?? { escalated_tasks: [], failed_orders: [] },
      });
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-stone-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-4 text-sm text-stone-500 text-center">Unable to load status</div>;
  }

  const attentionCount = (data.needs_attention.escalated_tasks.length + data.needs_attention.failed_orders.length);

  return (
    <div className="p-3 space-y-3 overflow-y-auto">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <StatTile icon={<ListTodo className="w-4 h-4 text-blue-600" />} label="Active Tasks" value={data.active_tasks} />
        <StatTile icon={<Package className="w-4 h-4 text-purple-600" />} label="Processing" value={data.orders_processing} />
        <StatTile icon={<CheckCircle className="w-4 h-4 text-green-600" />} label="Success (24h)" value={`${data.success_rate_24h.toFixed(0)}%`} />
        <StatTile
          icon={<AlertTriangle className={`w-4 h-4 ${attentionCount > 0 ? 'text-red-500' : 'text-stone-400'}`} />}
          label="Needs Attention"
          value={attentionCount}
          alert={attentionCount > 0}
        />
      </div>

      {/* Attention Items */}
      {attentionCount > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Needs Attention</p>
          {data.needs_attention.escalated_tasks.map((t) => (
            <Link
              key={t.id}
              to={`/admin/agent/tasks/${t.id}`}
              className="block p-2 rounded-lg bg-red-50 border border-red-100 hover:bg-red-100 transition-colors"
            >
              <span className="text-xs font-mono text-red-800">{t.task_type}</span>
              {t.error && <p className="text-[11px] text-red-600 truncate mt-0.5">{t.error}</p>}
            </Link>
          ))}
          {data.needs_attention.failed_orders.map((o) => (
            <Link
              key={o.id}
              to="/admin/supplier-orders"
              className="block p-2 rounded-lg bg-orange-50 border border-orange-100 hover:bg-orange-100 transition-colors"
            >
              <span className="text-xs text-orange-800">
                {o.order_number ? `Order #${o.order_number}` : 'Failed order'}
              </span>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Links */}
      <div className="pt-2 border-t border-stone-100 space-y-1">
        <Link to="/admin/agent" className="block text-xs text-green-700 hover:text-green-900 font-medium">Dashboard &rarr;</Link>
        <Link to="/admin/agent/tasks" className="block text-xs text-green-700 hover:text-green-900 font-medium">All Tasks &rarr;</Link>
        <Link to="/admin/agent/notifications" className="block text-xs text-green-700 hover:text-green-900 font-medium">Notifications &rarr;</Link>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, alert }: { icon: React.ReactNode; label: string; value: number | string; alert?: boolean }) {
  return (
    <div className={`p-2 rounded-lg border ${alert ? 'border-red-200 bg-red-50' : 'border-stone-100 bg-stone-50'}`}>
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[11px] text-stone-500">{label}</span>
      </div>
      <p className={`text-lg font-bold mt-0.5 ${alert ? 'text-red-700' : 'text-stone-800'}`}>{value}</p>
    </div>
  );
}
